import DataTable from "@/components/data-table";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useGetEncryptionData } from "@/hooks/use-postmaster";
import { createFileRoute, useSearch } from "@tanstack/react-router";
import { type ColDef } from "ag-grid-community";
import { format } from "date-fns";
import { Loader2, Lock, LockKeyhole } from "lucide-react";
import { useMemo } from "react";
import {
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

function EncryptionTabContent({
  date,
  domain,
}: {
  date: string;
  domain: string;
}) {
  const { startDate, endDate } = useMemo(() => {
    const end = new Date();
    const start = new Date();

    switch (date) {
      case "last7days":
        start.setDate(end.getDate() - 7);
        break;
      case "last30days":
        start.setDate(end.getDate() - 30);
        break;
      case "last60days":
        start.setDate(end.getDate() - 60);
        break;
      case "last90days":
        start.setDate(end.getDate() - 90);
        break;
      case "last120days":
        start.setDate(end.getDate() - 120);
        break;
      default:
        start.setDate(end.getDate() - 7);
    }

    return { startDate: start, endDate: end };
  }, [date]);

  const { data, isLoading } = useGetEncryptionData({
    domain,
    startDate,
    endDate,
  });

  const columnDefs: ColDef[] = [
    {
      field: "date",
      headerName: "Date",
      filter: true,
      sortable: true,
      valueFormatter: (params) =>
        format(new Date(params.value), "MMM dd, yyyy"),
    },
    {
      field: "inbound",
      headerName: "Inbound TLS rate",
      filter: true,
      sortable: true,
      valueFormatter: (params) =>
        `${(params.value == null || params.value === 0 ? 100 : params.value).toFixed(1)}%`,
    },
    {
      field: "outbound",
      headerName: "Outbound TLS rate",
      filter: true,
      sortable: true,
      valueFormatter: (params) =>
        params.value == null || params.value === 0
          ? "-"
          : `${params.value.toFixed(1)}%`,
    },
  ];

  const formatInboundRate = (value: number | null | undefined) =>
    value == null || value === 0 ? 100 : value;

  const formattedChartData = data?.chartData.map((item) => ({
    ...item,
    date: format(new Date(item.date), "MMM dd, yyyy"),
    inbound: formatInboundRate(item.inbound),
    outbound:
      item.outbound == null || item.outbound === 0 ? null : item.outbound,
  }));

  const inboundDisplay =
    data?.statistics.inbound.current == null ||
    data?.statistics.inbound.current === 0
      ? 100
      : data?.statistics.inbound.current;
  const outboundDisplay =
    data?.statistics.outbound.current == null ||
    data?.statistics.outbound.current === 0
      ? null
      : data?.statistics.outbound.current;

  if (isLoading)
    return (
      <div className="flex justify-center items-center h-[50vh] w-full text-foreground">
        <Loader2 size={20} className="animate-spin" />
      </div>
    );

  return (
    <div className="space-y-4">
      <div className="grid gap-4 grid-cols-1 md:grid-cols-2">
        <Card>
          <CardHeader className="p-4 pb-2">
            <CardTitle className="text-sm font-medium">
              Inbound TLS Encryption
            </CardTitle>
          </CardHeader>
          <CardContent className="p-4 pt-0">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-2xl font-bold">
                  {inboundDisplay.toFixed(1)}%
                </div>
                <p className="text-xs text-muted-foreground">
                  {inboundDisplay === 100
                    ? "All incoming traffic encrypted"
                    : "Some traffic unencrypted"}
                </p>
              </div>
              <Lock
                className={`h-5 w-5 ${
                  data?.statistics.inbound.status === "Good"
                    ? "text-blue-500"
                    : "text-yellow-500"
                }`}
              />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="p-4 pb-2">
            <CardTitle className="text-sm font-medium">
              Outbound TLS Encryption
            </CardTitle>
          </CardHeader>
          <CardContent className="p-4 pt-0">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-2xl font-bold">
                  {outboundDisplay == null
                    ? "-"
                    : `${outboundDisplay.toFixed(1)}%`}
                </div>
                <p className="text-xs text-muted-foreground">
                  {outboundDisplay == null || outboundDisplay !== 100
                    ? "Some traffic unencrypted"
                    : "All outgoing traffic encrypted"}
                </p>
              </div>
              <LockKeyhole
                className={`h-5 w-5 ${
                  data?.statistics.outbound.status === "Good"
                    ? "text-blue-500"
                    : "text-yellow-500"
                }`}
              />
            </div>
          </CardContent>
        </Card>
      </div>
      <Card>
        <CardHeader className="p-4">
          <CardTitle>Encrypted Traffic</CardTitle>
        </CardHeader>
        <CardContent className="p-4 pt-0">
          <div className="h-[300px]">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart
                data={formattedChartData}
                margin={{ top: 5, right: 20, bottom: 5, left: 0 }}
              >
                <XAxis
                  dataKey="date"
                  tickLine={false}
                  axisLine={false}
                  tick={{ fontSize: 12 }}
                  tickMargin={10}
                />
                <YAxis
                  tickLine={false}
                  axisLine={false}
                  ticks={[0, 20, 40, 60, 80, 100]}
                  domain={[0, 100]}
                  tickFormatter={(value) => `${value}%`}
                  tick={{ fontSize: 12 }}
                />
                <Tooltip
                  contentStyle={{
                    background: "hsl(var(--background))",
                    border: "1px solid #e5e7eb",
                    borderRadius: "4px",
                  }}
                  formatter={(value: number, name: string) => {
                    const formatted =
                      name === "Inbound TLS rate"
                        ? `${(value == null || value === 0 ? 100 : value).toFixed(1)}%`
                        : value == null || value === 0
                          ? "-"
                          : `${value.toFixed(1)}%`;
                    return [formatted, name];
                  }}
                  labelFormatter={(label) =>
                    format(new Date(label), "MMM dd, yyyy")
                  }
                />
                <Line
                  type="monotone"
                  dataKey="inbound"
                  name="Inbound TLS rate"
                  stroke="#3B82F6"
                  strokeWidth={2}
                  dot={{ fill: "#3B82F6", strokeWidth: 2, r: 4 }}
                />
                <Line
                  type="monotone"
                  dataKey="outbound"
                  name="Outbound TLS rate"
                  stroke="#EF4444"
                  strokeWidth={2}
                  dot={{ fill: "#EF4444", strokeWidth: 2, r: 4 }}
                />
                <Legend />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>
      <div className="mt-6">
        <DataTable
          rowData={data?.chartData || []}
          columnDefs={columnDefs}
          height={400}
        />
      </div>
    </div>
  );
}

// Export both the component for use in other files and the Route for the router
export { EncryptionTabContent };

function EncryptionTabRouteComponent() {
  // Get domain and date from search params if available, otherwise use defaults
  const search = useSearch({ strict: false });
  const domain = (search as { domain?: string })?.domain || "";
  const date = (search as { date?: string })?.date || "last7days";
  return <EncryptionTabContent date={date} domain={domain} />;
}

export const Route = createFileRoute(
  "/_private/postmaster/tabs/encryption-tab"
)({
  component: EncryptionTabRouteComponent,
});
